import React, { useRef } from 'react';
import { FaTimes, FaSlidersH, FaCalendarAlt, FaPercentage, FaDownload, FaUpload, FaUndo, FaMoon, FaSun, FaLayerGroup } from 'react-icons/fa';
import { DEFAULT_EXPENSES, DEFAULT_INCOME_STREAMS, DEFAULT_CREDIT_CARD, getTodayDateStr } from './billTrackerData';

const ParametersModal = ({
  config,
  setConfig,
  theme,
  setTheme,
  expenses,
  incomeStreams,
  creditCard,
  setExpenses,
  setIncomeStreams,
  setCreditCard,
  onClose
}) => {
  const fileInputRef = useRef(null);

  // Reset to default demo data
  const handleResetDemoData = () => {
    if (window.confirm('Reset all expenses, income streams, and credit card ledger to demo defaults? This will overwrite your changes.')) {
      setExpenses(DEFAULT_EXPENSES);
      setIncomeStreams(DEFAULT_INCOME_STREAMS);
      if (setCreditCard) setCreditCard(DEFAULT_CREDIT_CARD);
      setConfig(prev => ({ ...prev, referenceDate: getTodayDateStr(), autoSyncRealDate: true, safeDebtRatio: 0.30 }));
      alert('Ledger and Credit Card have been reset to demo data!');
      onClose();
    }
  };

  // Export data as JSON
  const handleExportJSON = () => {
    const backup = {
      version: '2.0',
      exportDate: new Date().toISOString(),
      config,
      expenses,
      incomeStreams,
      creditCard
    };
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(backup, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `bill_tracker_backup_${config.referenceDate}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  // Import JSON backup
  const handleImportJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        if (parsed.expenses && parsed.incomeStreams) {
          setExpenses(parsed.expenses);
          setIncomeStreams(parsed.incomeStreams);
          if (parsed.creditCard && setCreditCard) setCreditCard(parsed.creditCard);
          if (parsed.config) setConfig(parsed.config);
          alert('Financial data successfully restored!');
          onClose();
        } else {
          alert('Invalid backup file format.');
        }
      } catch (err) {
        alert('Failed to parse JSON file.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="bt-modal-backdrop" onClick={onClose}>
      <div className="bt-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '580px' }}>
        <div className="bt-modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <FaSlidersH style={{ color: '#4f46e5' }} /> Model Parameters & Settings
          </h3>
          <button className="bt-icon-btn" onClick={onClose}>
            <FaTimes size={18} />
          </button>
        </div>

        <div className="bt-modal-body">
          {/* Reference Date */}
          <div className="bt-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <FaCalendarAlt style={{ color: '#4f46e5' }} /> System Reference Date
              </label>
              <button
                type="button"
                className="bt-btn-secondary"
                style={{ fontSize: '0.75rem', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer' }}
                onClick={() => setConfig({ ...config, referenceDate: getTodayDateStr(), autoSyncRealDate: true })}
                title="Reset reference date to today's real calendar date"
              >
                Set to Today ({getTodayDateStr()})
              </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="date"
                className="bt-input"
                style={{ flex: 1 }}
                value={config.referenceDate}
                onChange={(e) => setConfig({ ...config, referenceDate: e.target.value, autoSyncRealDate: false })}
              />
            </div>

            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="autoSyncDate"
                checked={config.autoSyncRealDate !== false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setConfig(prev => ({
                    ...prev,
                    autoSyncRealDate: isChecked,
                    referenceDate: isChecked ? getTodayDateStr() : prev.referenceDate
                  }));
                }}
                style={{ cursor: 'pointer', accentColor: '#4f46e5' }}
              />
              <label htmlFor="autoSyncDate" style={{ fontSize: '0.82rem', cursor: 'pointer', margin: 0, fontWeight: 500, color: '#334155' }}>
                Auto-sync with Real Date (Always stays on current calendar day)
              </label>
            </div>

            <div className="bt-subtext" style={{ marginTop: '5px' }}>
              {config.autoSyncRealDate !== false ? (
                <span style={{ color: '#059669', fontWeight: 600 }}>
                  ✓ Live Mode Active: Automatically tracks real daily calendar dates. Bills with due dates prior to today are flagged as Overdue.
                </span>
              ) : (
                <span style={{ color: '#d97706', fontWeight: 600 }}>
                  ⚠ Simulation Mode: Frozen on manual date ({config.referenceDate}). Check "Auto-sync" or click "Set to Today" to track real time.
                </span>
              )}
            </div>
          </div>

          {/* Safe Debt Ratio Cap */}
          <div className="bt-form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPercentage style={{ color: '#059669' }} /> Safe Debt Ceiling Ratio ({Math.round(config.safeDebtRatio * 100)}%)
            </label>
            <input
              type="range"
              min="0.10"
              max="0.50"
              step="0.05"
              style={{ width: '100%', accentColor: '#4f46e5', cursor: 'pointer' }}
              value={config.safeDebtRatio}
              onChange={(e) => setConfig({ ...config, safeDebtRatio: parseFloat(e.target.value) })}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748b' }}>
              <span>10% (Ultra Conservative)</span>
              <span>30% (Standard Benchmark)</span>
              <span>50% (High Leverage)</span>
            </div>
          </div>

          {/* Monthly Category Spending Budgets */}
          <div className="bt-form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaLayerGroup style={{ color: '#4f46e5' }} /> Monthly Category Spending Budgets ($)
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '6px' }}>
              <div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Business ($)</span>
                <input
                  type="number"
                  className="bt-input"
                  style={{ marginTop: '4px' }}
                  value={config.categoryBudgets?.Business ?? 12000}
                  onChange={(e) => setConfig({
                    ...config,
                    categoryBudgets: {
                      ...(config.categoryBudgets || {}),
                      Business: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Household ($)</span>
                <input
                  type="number"
                  className="bt-input"
                  style={{ marginTop: '4px' }}
                  value={config.categoryBudgets?.Household ?? 5000}
                  onChange={(e) => setConfig({
                    ...config,
                    categoryBudgets: {
                      ...(config.categoryBudgets || {}),
                      Household: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Others ($)</span>
                <input
                  type="number"
                  className="bt-input"
                  style={{ marginTop: '4px' }}
                  value={config.categoryBudgets?.Others ?? 2000}
                  onChange={(e) => setConfig({
                    ...config,
                    categoryBudgets: {
                      ...(config.categoryBudgets || {}),
                      Others: parseFloat(e.target.value) || 0
                    }
                  })}
                />
              </div>
            </div>
            <div className="bt-subtext" style={{ marginTop: '4px' }}>
              Used to measure spend variance and warn when commitments exceed budget limits.
            </div>
          </div>

          {/* Theme Toggle */}
          <div className="bt-form-group" style={{ paddingTop: '8px' }}>
            <label>Dashboard Appearance</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className={`bt-btn-secondary ${theme === 'light' ? 'active' : ''}`}
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  borderColor: theme === 'light' ? '#4f46e5' : '#cbd5e1',
                  background: theme === 'light' ? '#ede9fe' : '#ffffff',
                  color: theme === 'light' ? '#4f46e5' : '#334155'
                }}
                onClick={() => setTheme('light')}
              >
                <FaSun /> Clean Sheet (Light)
              </button>
              <button
                type="button"
                className={`bt-btn-secondary ${theme === 'dark' ? 'active' : ''}`}
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  borderColor: theme === 'dark' ? '#4f46e5' : '#cbd5e1',
                  background: theme === 'dark' ? '#1e1b4b' : '#ffffff',
                  color: theme === 'dark' ? '#a5b4fc' : '#334155'
                }}
                onClick={() => setTheme('dark')}
              >
                <FaMoon /> Dark Executive
              </button>
            </div>
          </div>

          {/* Data Backup & Restore */}
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
              Ledger Data Management
            </label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="bt-btn-secondary"
                style={{ fontSize: '0.82rem', padding: '8px 14px' }}
                onClick={handleExportJSON}
              >
                <FaDownload /> Export Backup (JSON)
              </button>

              <button
                type="button"
                className="bt-btn-secondary"
                style={{ fontSize: '0.82rem', padding: '8px 14px' }}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <FaUpload /> Restore Backup
              </button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".json"
                onChange={handleImportJSON}
              />

              <button
                type="button"
                className="bt-btn-secondary"
                style={{ fontSize: '0.82rem', padding: '8px 14px', color: '#dc2626' }}
                onClick={handleResetDemoData}
              >
                <FaUndo /> Reset Demo Data
              </button>
            </div>
          </div>
        </div>

        <div className="bt-modal-footer">
          <button className="bt-btn-primary" onClick={onClose}>
            Apply & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParametersModal;
